import React, { useEffect, useRef, useState } from 'react';
import { IconName } from '../../assets/icons';
import colors from '../../constants/colors';
import Colors from '../../constants/colors';
import Icon from '../general/Icon';
import DragIndicator from './DragIndicator';
import ResizeIndicator from './ResizeIndicator';

export interface WindowProps {
    closeWindow: () => void;
    minimizeWindow: () => void;
    onInteract: () => void;
    width: number;
    height: number;
    top: number;
    left: number;
    windowTitle?: string;
    bottomLeftText?: string;
    rainbow?: boolean;
    windowBarColor?: string;
    windowBarIcon?: IconName;
    onWidthChange?: (width: number) => void;
    onHeightChange?: (height: number) => void;
}

const Window: React.FC<WindowProps> = (props) => {
    const windowRef = useRef<any>(null);
    const dragRef = useRef<any>(null);
    const contentRef = useRef<any>(null);

    const dragProps = useRef<{
        dragStartX: any;
        dragStartY: any;
    }>();

    const resizeRef = useRef<any>(null);

    const [top, setTop] = useState(props.top);
    const [left, setLeft] = useState(props.left);

    const lastClickInside = useRef(false);

    const [width, setWidth] = useState(props.width);
    const [height, setHeight] = useState(props.height);

    const [contentWidth, setContentWidth] = useState(props.width);
    const [contentHeight, setContentHeight] = useState(props.height);

    const [windowActive, setWindowActive] = useState(true);

    const [isMaximized, setIsMaximized] = useState(false);
    const [preMaxSize, setPreMaxSize] = useState({
        width,
        height,
        top,
        left,
    });

    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);

    const startResize = (event: any) => {
        event.preventDefault();
        setIsResizing(true);
        window.addEventListener('mousemove', onResize, false);
        window.addEventListener('mouseup', stopResize, false);
    };

    const onResize = ({ clientX, clientY }: any) => {
        const curWidth = clientX - left;
        const curHeight = clientY - top;
        if (curWidth > 520) resizeRef.current.style.width = `${curWidth}px`;
        if (curHeight > 220) resizeRef.current.style.height = `${curHeight}px`;
        resizeRef.current.style.opacity = 1;
    };

    const stopResize = () => {
        setIsResizing(false);
        setWidth(resizeRef.current.style.width);
        setHeight(resizeRef.current.style.height);
        resizeRef.current.style.opacity = 0;
        window.removeEventListener('mousemove', onResize, false);
        window.removeEventListener('mouseup', stopResize, false);
    };

    const startDrag = (event: any) => {
        const { clientX, clientY } = event;
        setIsDragging(true);
        event.preventDefault();
        dragProps.current = {
            dragStartX: clientX,
            dragStartY: clientY,
        };
        window.addEventListener('mousemove', onDrag, false);
        window.addEventListener('mouseup', stopDrag, false);
    };

    const onDrag = ({ clientX, clientY }: any) => {
        let { x, y } = getXYFromDragProps(clientX, clientY);
        dragRef.current.style.transform = `translate(${x}px, ${y}px)`;
        dragRef.current.style.opacity = 1;
    };

    const stopDrag = ({ clientX, clientY }: any) => {
        setIsDragging(false);
        // dragRef.current.style.opacity = 0;
        const { x, y } = getXYFromDragProps(clientX, clientY);
        setTop(y);
        setLeft(x);
        window.removeEventListener('mousemove', onDrag, false);
        window.removeEventListener('mouseup', stopDrag, false);
    };

    const getXYFromDragProps = (
        clientX: number,
        clientY: number
    ): { x: number; y: number } => {
        if (!dragProps.current) return { x: 0, y: 0 };
        const { dragStartX, dragStartY } = dragProps.current;

        const x = clientX - dragStartX + left;
        const y = clientY - dragStartY + top;

        return { x, y };
    };

    useEffect(() => {
        dragRef.current.style.transform = `translate(${left}px, ${top}px)`;
    });

    useEffect(() => {
        props.onWidthChange && props.onWidthChange(contentWidth);
    }, [props.onWidthChange, contentWidth]); // eslint-disable-line

    useEffect(() => {
        props.onHeightChange && props.onHeightChange(contentHeight);
    }, [props.onHeightChange, contentHeight]); // eslint-disable-line

    useEffect(() => {
        setContentWidth(contentRef.current.getBoundingClientRect().width);
    }, [width]);

    useEffect(() => {
        setContentHeight(contentRef.current.getBoundingClientRect().height);
    }, [height]);

    const maximize = () => {
        if (isMaximized) {
            setWidth(preMaxSize.width);
            setHeight(preMaxSize.height);
            setTop(preMaxSize.top);
            setLeft(preMaxSize.left);
            setIsMaximized(false);
        } else {
            setPreMaxSize({
                width,
                height,
                top,
                left,
            });
            setWidth(window.innerWidth);
            setHeight(window.innerHeight - 32);
            setTop(0);
            setLeft(0);
            setIsMaximized(true);
        }
    };

    const onCheckClick = () => {
        if (lastClickInside.current) {
            setWindowActive(true);
        } else {
            setWindowActive(false);
        }
        lastClickInside.current = false;
    };

    useEffect(() => {
        window.addEventListener('mousedown', onCheckClick, false);
        return () => {
            window.removeEventListener('mousedown', onCheckClick, false);
        };
    }, []);

    const onWindowInteract = () => {
        props.onInteract();
        setWindowActive(true);
        lastClickInside.current = true;
    };

    return (
        <div onMouseDown={onWindowInteract} style={styles.container}>
            <div
                style={Object.assign({}, styles.window, {
                    width,
                    height,
                    top,
                    left,
                })}
                ref={windowRef}
            >
                <div style={styles.windowBorderOuter}>
                    <div style={styles.windowBorderInner}>
                        <div
                            style={styles.dragHitbox}
                            onMouseDown={startDrag}
                        ></div>
                        <div
                            className={props.rainbow ? 'rainbow-wrapper' : ''}
                            style={Object.assign(
                                {},
                                styles.topBar,
                                props.windowBarColor && {
                                    backgroundColor: props.windowBarColor,
                                }
                            )}
                        >
                            <div style={styles.windowHeader}>
                                {(props.windowBarIcon !== 'chrome') &&
                                    <>
                                        {props.windowBarIcon ? (
                                            <Icon
                                                icon={props.windowBarIcon}
                                                style={Object.assign(
                                                    {},
                                                    styles.windowBarIcon,
                                                    !windowActive && { opacity: 0.5 }
                                                )}
                                                size={16}
                                            />
                                        ) : (
                                            <div style={{ width: 16 }} />
                                        )}
                                        <p
                                            style={
                                                windowActive
                                                    ? {}
                                                    : { color: colors.lightGray }
                                            }
                                            className="showcase-header"
                                        >
                                            {props.windowTitle}
                                        </p>
                                    </>
                                }
                                {(props.windowBarIcon === 'chrome') &&
                                    <div style={styles.pestana}>
                                        <div style={styles.pestanaBefore}></div>
                                        <p style={styles.pestanaText}>Portafolio Jose</p>
                                        <div style={styles.pestanaAfter}></div>
                                    </div>
                                }
                            </div>
                            <div style={styles.windowTopButtons}>
                                <button type="button" onClick={props.minimizeWindow} style={{ ...styles.buttonsWindowOption, ...styles.buttonsWindowOption1, border: 'none', background: 'transparent', cursor: 'pointer' }} className='buttonsWindowOption' aria-label="Minimize Window">
                                    <img alt="Minimize" style={{ ...styles.buttonsWindowOptionImage, ...styles.buttonsWindowOption1Img }} data-v-27b9ddf6="" src="data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' width='12' height='5' fill='none' viewBox='0 0 12 5'%3e%3cpath fill='url(%23a)' d='M0 0h12v5H0z'/%3e%3cdefs%3e%3cpattern id='a' width='1' height='1' patternContentUnits='objectBoundingBox'%3e%3cuse xlink:href='%23b' transform='scale(.08333 .2)'/%3e%3c/pattern%3e%3cimage xlink:href='data:image/png%3bbase64%2ciVBORw0KGgoAAAANSUhEUgAAAAwAAAAFCAYAAABxeg0vAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAA7SURBVHgBlY%2bxDQAgDMNaiQ9B8AHcxAlI/NYtKwj6QOohUzxYc%2b1HAqQ3e03qXNpwwcyE5QsAaEGjDRf8ZAza6Bz6VQAAAABJRU5ErkJggg==' id='b' width='12' height='5' /%3e%3c/defs%3e%3c/svg%3e" />
                                </button>
                                <button type="button" onClick={maximize} style={{ ...styles.buttonsWindowOption, border: 'none', background: 'transparent', cursor: 'pointer' }} className='buttonsWindowOption' aria-label="Maximize Window">
                                    <img alt="Maximize" style={styles.buttonsWindowOptionImage} data-v-27b9ddf6="" src="data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' width='17' height='15' fill='none' viewBox='0 0 17 15'%3e%3cpath fill='url(%23a)' d='M0 0h17v15H0z'/%3e%3cdefs%3e%3cpattern id='a' width='1' height='1' patternContentUnits='objectBoundingBox'%3e%3cuse xlink:href='%23b' transform='matrix(.08824 0 0 .1 -.03 0)'/%3e%3c/pattern%3e%3cimage xlink:href='data:image/png%3bbase64%2ciVBORw0KGgoAAAANSUhEUgAAAAwAAAAKCAYAAACALL/6AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAABsSURBVHgBlZHBCYAwDEVT6YaKbqArOILgBLqBosP1VkhAaQ6S1lKad%2bihPy8kxLT9%2bIACG57r2KqKu2GCBpTYtEMOOQELzrnv4z53I4vDjjJnwXsPJWTOAiJGHVNB5pGwLjPk%2bAlEBLUY7eFebCosBHOR7vYAAAAASUVORK5CYII=' id='b' width='12' height='10' /%3e%3c/defs%3e%3c/svg%3e" />
                                </button>
                                <button
                                    type="button"
                                    onClick={props.closeWindow}
                                    style={{ ...styles.buttonsWindowOption, ...styles.buttonsWindowOption3, border: 'none', background: 'transparent', cursor: 'pointer' }}
                                    className='buttonsWindowOption buttonsWindowOption3'
                                    aria-label="Close Window"
                                >
                                    <img alt="Close" style={styles.buttonsWindowOptionImage} data-v-27b9ddf6="" src="data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' width='14' height='13' fill='none' viewBox='0 0 14 13'%3e%3cpath fill='url(%23a)' d='M0 0h14v13H0z'/%3e%3cdefs%3e%3cpattern id='a' width='1' height='1' patternContentUnits='objectBoundingBox'%3e%3cuse xlink:href='%23b' transform='matrix(.09286 0 0 .1 -.01 0)'/%3e%3c/pattern%3e%3cimage xlink:href='data:image/png%3bbase64%2ciVBORw0KGgoAAAANSUhEUgAAAAsAAAAKCAYAAABi8KSDAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAACUSURBVHgBlZFNCoQwDIVbmRvOMHOAAd0K/uDGlYKiCG4VPICi9yu0oCSQoFUXFtKEl6%2bvoZXvr7sKay1TL690FOexY%2bHz87i2decOuDKQsNGVe8AGYTRJIh0Y%2borBvxszCPllOymlxN06jNE26Qnwg4zdHQLrMhFaawwAqAadDPE1ijwUxhiMKCmxCZk06OMYTz5lAx3xWDeXkLNAAAAAAElFTkSuQmCC' id='b' width='11' height='10' /%3e%3c/defs%3e%3c/svg%3e" />
                                </button>
                            </div>
                        </div>
                        <div
                            style={Object.assign({}, styles.contentOuter)}
                        >
                            <div style={styles.contentInner}>
                                <div style={styles.content} ref={contentRef}>
                                    {props.children}
                                </div>
                            </div>
                        </div>
                        <div
                            onMouseDown={startResize}
                            style={styles.resizeHitbox}
                        ></div>
                    </div>
                </div>
            </div>

            <div
                style={
                    !isResizing
                        ? {
                            zIndex: -10000,
                            pointerEvents: 'none',
                        }
                        : {
                            zIndex: 1000,
                            cursor: 'nwse-resize',
                            mixBlendMode: 'difference',
                        }
                }
            >
                <ResizeIndicator
                    top={top}
                    left={left}
                    width={width}
                    height={height}
                    resizeRef={resizeRef}
                />
            </div>
            <div
                style={
                    !isDragging
                        ? {
                            zIndex: -10000,
                            pointerEvents: 'none',
                        }
                        : {
                            zIndex: 1000,
                            cursor: 'move',
                            mixBlendMode: 'difference',
                        }
                }
            >
                <DragIndicator
                    width={width}
                    height={height}
                    dragRef={dragRef}
                />
            </div>
        </div>
    );
};

const styles: StyleSheetCSS = {
    window: {
        backgroundImage: 'linear-gradient(135deg,hsla(0,0%,100%,.3333333333) 70px,transparent 150px),linear-gradient(225deg,hsla(0,0%,100%,.3333333333) 70px,transparent 150px),linear-gradient(transparent 30px,#fff 39%,transparent 40%)',
        backgroundColor: 'rgba(170,209,251,0.55)',
        position: 'absolute',
        backdropFilter: 'blur(2px)',
        borderRadius: 7,
    },
    dragHitbox: {
        position: 'absolute',
        width: 'calc(100% - 70px)',
        height: 48,
        zIndex: 10000,
        top: -8,
        left: -4,
        cursor: 'move',
    },
    windowBorderOuter: {
        border: `1px solid #000000`,
        borderTopColor: colors.lightGray,
        borderLeftColor: colors.lightGray,
        flex: 1,
        borderRadius: 7
    },
    windowBorderInner: {
        border: `1px solid #ffffff`,
        borderTopColor: colors.white,
        borderLeftColor: colors.white,
        flex: 1,
        padding: 5,

        flexDirection: 'column',
        borderRadius: 7
    },
    resizeHitbox: {
        position: 'absolute',
        width: 60,
        height: 60,
        bottom: -20,
        right: -20,
        cursor: 'nwse-resize',
    },
    topBar: {
        backgroundColor: 'transparent',
        width: '100%',
        height: 20,
        alignItems: 'center',
        paddingRight: 2,
        boxSizing: 'border-box',
        position: 'relative'
    },
    contentOuter: {
        border: `1px solid ${Colors.white}`,
        borderTopColor: colors.darkGray,
        borderLeftColor: colors.darkGray,
        flexGrow: 1,

        marginTop: 8,
        marginBottom: 1,
        overflow: 'hidden',
    },
    contentInner: {
        border: `1px solid ${Colors.lightGray}`,
        flex: 1,
        overflow: 'hidden',
    },
    content: {
        flex: 1,

        position: 'relative',
        // overflow: 'scroll',
        overflowX: 'hidden',
        backgroundColor: Colors.white,
    },
    bottomBar: {
        flexShrink: 1,
        width: '100%',
        height: 20,
    },
    bottomSpacer: {
        width: 16,
        marginLeft: 2,
    },
    insetBorder: {
        border: `1px solid ${Colors.white}`,
        borderTopColor: colors.darkGray,
        borderLeftColor: colors.darkGray,
        padding: 2,
    },
    bottomResizeContainer: {
        flex: 2 / 7,

        justifyContent: 'flex-end',
        padding: 0,
        marginLeft: 2,
    },
    windowTopButtons: {
        position: 'absolute',
        zIndex: 1000000,
        top: -5,
        right: 5,
        boxShadow: '0 0 0 1px hsla(0,0%,100%,.6666666667)',
        border: '1px solid rgba(0,0,0,.6666666667)',
        borderTopWidth: 0,
        borderRadius: '0px 0px 5px 5px'
    },
    windowHeader: {
        flex: 1,
        // justifyContent: 'center',
        // alignItems: 'center',
    },
    windowBarIcon: {
        paddingLeft: 4,
        paddingRight: 4,
    },
    buttonsWindowOption: {
        width: 29,
        height: 19,
        backgroundImage: 'linear-gradient(hsla(0, 0%, 95.7%, .5725490196), hsla(0, 0%, 82.7%, .4549019608) 45%, rgba(0, 0, 0, .1803921569) 50%, rgba(143, 150, 173, .3058823529))',
        borderRight: '1px solid rgba(0, 0, 0, .6666666667)',
        justifyContent: 'center',
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        position: 'relative'

    },
    buttonsWindowOptionImage: {
        height: 11,
        width: 14,
        position: 'relative',
        zIndex: 2
    },
    buttonsWindowOption1: {
        alignItems: 'end'
    },
    buttonsWindowOption3: {
        width: 48,
        backgroundColor: '#d54f36',
        backgroundImage: 'linear-gradient(hsla(0,0%,95.7%,.5725490196),hsla(0,0%,82.7%,.4549019608) 45%,rgba(0,0,0,.1803921569) 50%,rgba(143,150,173,.3058823529)),radial-gradient(circle at -60% 50%,rgba(0,0,0,.3333333333) 5% 10%,transparent 50%),radial-gradient(circle at 160% 50%,rgba(0,0,0,.3333333333) 5% 10%,transparent 50%)',
        borderBottomRightRadius: 3
    },
    buttonsWindowOption1Img: {
        paddingBottom: 2
    },
    pestana: {
        margin: '0 12px',
        padding: '0 3px',
        background: '#fff',
        borderTop: '1px solid rgba(0, 0, 0, .3333333333)',
        position: 'relative',
        height: 25,
        alignItems: 'center',
        marginBottom: -10,
        marginLeft: 20
    },
    pestanaText: {
        fontSize: 14,
        zIndex: 3
    },
    pestanaAfter: {
        position: 'absolute',
        top: 0,
        right: '-10px',
        width: '15px',
        height: '25px',
        zIndex: 0,
        background: '#fff',
        transform: 'skewX(23deg)',
    },
    pestanaBefore: {
        position: 'absolute',
        top: 0,
        left: '-10px',
        width: '15px',
        height: '25px',
        zIndex: 0,
        background: '#fff',
        transform: 'skewX(-23deg)'
    },
};

export default Window;
